// TwoEndsDemos — the dementia-vs-kids split on the unified landing. Two
// cards visually together but distinctly themed, each linking directly to
// its full demo (John → /demo/john, Ella → /demo/ella). This is where
// curious visitors decide which story to press play on first.
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Play, Coffee, PawPrint, Sparkle } from "@phosphor-icons/react";
import { ForgetMeNotIcon } from "../ForgetMeNotIcon";
import { FmnWatermark } from "../FmnWatermark";

const FADE = {
    initial: { opacity: 0, y: 22 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.95, ease: [0.16, 1, 0.3, 1] },
};

const JOHN_IMG = "/brand/product/adult-lavender-bedside.png";
const ELLA_IMG = "/product-mockup-kids-pink.png";

export function TwoEndsDemos() {
    const { t } = useTranslation();
    return (
        <section
            className="relative py-32 px-6 rem-petal-bg overflow-hidden"
            data-testid="two-ends-demos-section"
            id="demos"
        >
            <FmnWatermark position="left" size={800} opacity={0.05} rotate={-10} />
            <div className="max-w-7xl mx-auto">
                <motion.div {...FADE} className="text-center max-w-3xl mx-auto mb-16">
                    <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#6B7DB8] font-semibold mb-5">
                        {t("two_ends_demos.eyebrow")}
                    </p>
                    <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#2D2B2A] leading-[1.06] tracking-tight">
                        {t("two_ends_demos.headline_part1")}{" "}
                        <em className="italic text-[#4A5A95]">{t("two_ends_demos.headline_emphasis")}</em>
                    </h2>
                    <p className="mt-6 text-lg text-[#5C5A59] leading-relaxed font-light">
                        {t("two_ends_demos.lead")}
                    </p>
                </motion.div>

                {/* Press-play hint card — sets expectations before the demos */}
                <motion.div
                    {...FADE}
                    transition={{ ...FADE.transition, delay: 0.05 }}
                    className="max-w-3xl mx-auto mb-14 rounded-[2rem] bg-white/70 backdrop-blur border border-[#E5E1D8] px-7 py-6 sm:px-9 sm:py-7 shadow-rem-soft"
                    data-testid="demos-press-play-hint"
                >
                    <div className="flex items-start gap-4">
                        <div className="shrink-0 mt-0.5 w-10 h-10 rounded-full bg-[#F4F6FB] flex items-center justify-center">
                            <Coffee size={18} weight="duotone" color="#4A5A95" />
                        </div>
                        <div className="min-w-0">
                            <p className="font-sans text-[10px] uppercase tracking-[0.26em] text-[#6B7DB8] font-semibold mb-2">
                                {t("two_ends_demos.hint_eyebrow")}
                            </p>
                            <p className="text-[#5C5A59] leading-relaxed">
                                {t("two_ends_demos.hint_body")}
                            </p>
                            <p className="mt-3 text-sm text-[#8A8786] italic leading-relaxed">
                                {t("two_ends_demos.hint_video")}
                            </p>
                        </div>
                    </div>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-10 max-w-6xl mx-auto">
                    {/* ELDERS / John */}
                    <motion.div
                        {...FADE}
                        transition={{ ...FADE.transition, delay: 0.1 }}
                        data-testid="two-ends-card-john"
                    >
                        <Link
                            to="/demo/john"
                            className="group block relative overflow-hidden rounded-[2rem] border border-[#8C9ECB]/30 hover:border-[#6B7DB8] shadow-rem-soft hover:shadow-rem-card transition-all duration-500 hover:-translate-y-1 bg-gradient-to-br from-[#F4F6FB] via-white to-[#EDF0F8]"
                        >
                            <div className="relative aspect-[16/10] overflow-hidden">
                                <img
                                    src={JOHN_IMG}
                                    alt="John reminiscing with the lavender remembr tablet"
                                    className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[1100ms]"
                                    loading="lazy"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#1a1c25]/55 via-transparent to-transparent" />
                                <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/90 backdrop-blur text-[10px] font-semibold uppercase tracking-[0.22em] text-[#4A5A95]">
                                    <ForgetMeNotIcon size={12} />
                                    {t("two_ends_demos.elder_eyebrow")}
                                </div>
                                <div className="absolute bottom-4 left-4 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#6B7DB8] text-white text-xs font-semibold">
                                    <Play size={12} weight="fill" />
                                    Watch demo
                                </div>
                            </div>
                            <div className="p-8">
                                <h3 className="font-serif text-3xl text-[#2D2B2A] leading-tight">
                                    {t("two_ends_demos.elder_title")}
                                </h3>
                                <p className="mt-3 text-[#5C5A59] leading-relaxed">
                                    {t("two_ends_demos.elder_subtitle")}
                                </p>
                                <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#4A5A95] group-hover:text-[#2D2B2A] transition-colors">
                                    {t("two_ends_demos.elder_cta")}
                                    <ArrowRight weight="bold" size={16} className="group-hover:translate-x-1 transition-transform" />
                                </div>
                            </div>
                        </Link>
                    </motion.div>

                    {/* KIDS / Ella */}
                    <motion.div
                        {...FADE}
                        transition={{ ...FADE.transition, delay: 0.22 }}
                        data-testid="two-ends-card-ella"
                    >
                        <Link
                            to="/demo/ella"
                            className="group block relative overflow-hidden rounded-[2rem] border border-[#F4C7CC]/50 hover:border-[#E89AA1] shadow-rem-soft hover:shadow-rem-card transition-all duration-500 hover:-translate-y-1 bg-gradient-to-br from-[#FBF1F2] via-white to-[#FBE7E9]"
                        >
                            <div className="relative aspect-[16/10] overflow-hidden">
                                <img
                                    src={ELLA_IMG}
                                    alt="The pink remembr tablet with knit booties and a teddy — Ella's edition"
                                    className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[1100ms]"
                                    loading="lazy"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#A85D62]/40 via-transparent to-transparent" />
                                <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/90 backdrop-blur text-[10px] font-semibold uppercase tracking-[0.22em] text-[#A85D62]">
                                    <ForgetMeNotIcon size={12} />
                                    {t("two_ends_demos.kids_eyebrow")}
                                </div>
                                <div className="absolute bottom-4 left-4 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#A85D62] text-white text-xs font-semibold">
                                    <Play size={12} weight="fill" />
                                    Watch demo
                                </div>
                            </div>
                            <div className="p-8">
                                <h3 className="font-serif text-3xl text-[#2D2B2A] leading-tight">
                                    {t("two_ends_demos.kids_title")}
                                </h3>
                                <p className="mt-3 text-[#5C5A59] leading-relaxed">
                                    {t("two_ends_demos.kids_subtitle")}
                                </p>
                                <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#A85D62] group-hover:text-[#2D2B2A] transition-colors">
                                    {t("two_ends_demos.kids_cta")}
                                    <ArrowRight weight="bold" size={16} className="group-hover:translate-x-1 transition-transform" />
                                </div>
                            </div>
                        </Link>
                    </motion.div>

                    {/* PETS — coming-soon launch edition. Becomes a real Link
                        once Glenn's pet demo profile is live. The amber
                        gradient matches the planned Pet edition case colour. */}
                    <motion.div
                        {...FADE}
                        transition={{ ...FADE.transition, delay: 0.34 }}
                        data-testid="two-ends-card-pet"
                    >
                        <div
                            className="group block relative overflow-hidden rounded-[2rem] border border-[#E6B999]/50 shadow-rem-soft hover:shadow-rem-card transition-all duration-500 hover:-translate-y-1 bg-gradient-to-br from-[#FBEFE3] via-white to-[#F6E0CC]"
                            aria-label={t("two_ends_demos.pet_coming_soon", "Pet edition — coming this season")}
                        >
                            <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-[#D98E5F] to-[#C97B4A] flex items-center justify-center">
                                {/* Soft scattered paw motif */}
                                {[
                                    { x: "12%", y: "18%", s: 38, r: -14, o: 0.22 },
                                    { x: "78%", y: "22%", s: 30, r: 18,  o: 0.18 },
                                    { x: "26%", y: "70%", s: 26, r: 8,   o: 0.20 },
                                    { x: "70%", y: "74%", s: 34, r: -10, o: 0.18 },
                                    { x: "50%", y: "50%", s: 90, r: 0,   o: 0.95 },
                                ].map((p, i) => (
                                    <PawPrint
                                        key={i}
                                        size={p.s}
                                        weight={i === 4 ? "fill" : "regular"}
                                        color="#FFF8F0"
                                        style={{
                                            position: "absolute",
                                            left: p.x,
                                            top: p.y,
                                            transform: `translate(-50%, -50%) rotate(${p.r}deg)`,
                                            opacity: p.o,
                                        }}
                                        aria-hidden
                                    />
                                ))}
                                <div className="absolute inset-0 bg-gradient-to-t from-[#5A3A20]/35 via-transparent to-transparent" />
                                <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/90 backdrop-blur text-[10px] font-semibold uppercase tracking-[0.22em] text-[#8A4A2D]">
                                    <ForgetMeNotIcon size={12} />
                                    {t("two_ends_demos.pet_eyebrow", "For the family at our feet")}
                                </div>
                                <div className="absolute bottom-4 left-4 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/95 text-[#8A4A2D] text-xs font-semibold">
                                    <Sparkle size={12} weight="fill" />
                                    {t("two_ends_demos.pet_badge", "Coming this season")}
                                </div>
                            </div>
                            <div className="p-8">
                                <h3 className="font-serif text-3xl text-[#2D2B2A] leading-tight">
                                    {t("two_ends_demos.pet_title", "Meet the Pet edition")}
                                </h3>
                                <p className="mt-3 text-[#5C5A59] leading-relaxed">
                                    {t("two_ends_demos.pet_subtitle", "Their story while they're still here. Their voice, forever, when they're not.")}
                                </p>
                                <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#8A4A2D]">
                                    {t("two_ends_demos.pet_cta", "Demo arriving soon")}
                                    <PawPrint weight="fill" size={16} />
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
